import { useState, useEffect, useCallback, useRef } from "react";
import { pb } from "@/lib/pocketbase";
import { FreeAuditData, ComprehensiveEnhancementAudit } from "@/types/seo";
import { useBrand } from "@/contexts/brand-context";

/**
 * Custom hook for managing SEO audit data across different SEO components
 * Each section (Overview, Pagespeed, and Enhancements) uses its own API
 * and the backend handles pending audit processes
 */
export function useSEOAudit() {
  const { currentBrand, isLoading: isBrandLoading } = useBrand();

  // Basic SEO audit state
  const [audit, setAudit] = useState<FreeAuditData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Enhancement audit state
  const [enhancementAudit, setEnhancementAudit] =
    useState<ComprehensiveEnhancementAudit | null>(null);
  const [enhancementLoading, setEnhancementLoading] = useState(false);
  const [enhancementError, setEnhancementError] = useState<string | null>(null);

  // Cache references for free audit data (used across components)
  const freeAuditCacheRef = useRef<
    Map<string, { data: FreeAuditData; timestamp: number }>
  >(
    (() => {
      try {
        const stored = sessionStorage.getItem("freeAuditCache");
        return new Map(JSON.parse(stored || "[]"));
      } catch (err) {
        console.warn(
          "Failed to load free audit cache from sessionStorage:",
          err,
        );
        return new Map();
      }
    })(),
  );
  const SESSION_CACHE_DURATION = useRef(24 * 60 * 60 * 1000); // 24 hours cache for free audit (session lifetime)

  // Cache for enhancement audit data
  const enhancementCacheRef = useRef<
    Map<string, { audit: ComprehensiveEnhancementAudit; timestamp: number }>
  >(new Map());
  const ENHANCEMENT_CACHE_DURATION = useRef(24 * 60 * 60 * 1000); // 24 hours cache for enhancement audit

  /**
   * Fetch free audit data from the api/tools/audit endpoint
   * This provides basic SEO metrics for any URL
   */
  const fetchFreeAudit = useCallback(
    async (url: string): Promise<FreeAuditData | null> => {
      try {
        // Check session cache first - only call API once per session per URL
        const cached = freeAuditCacheRef.current.get(url);
        if (
          cached &&
          Date.now() - cached.timestamp < SESSION_CACHE_DURATION.current
        ) {
          console.log(
            `Using cached free audit data for ${url} (${Math.round((Date.now() - cached.timestamp) / (1000 * 60))} minutes old)`,
          );
          return cached.data;
        }

        console.log(`Fetching fresh free audit data for ${url}...`);

        const response = await fetch(`${pb.baseURL}/api/tools/audit`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ url }),
        });

        if (!response.ok) {
          console.error(
            `Free audit API error: ${response.status} ${response.statusText}`,
          );
          return null;
        }

        const freeAuditData: FreeAuditData = await response.json();

        // Cache the result for the entire session (24 hours)
        freeAuditCacheRef.current.set(url, {
          data: freeAuditData,
          timestamp: Date.now(),
        });

        // Persist cache to sessionStorage
        try {
          sessionStorage.setItem(
            "freeAuditCache",
            JSON.stringify(Array.from(freeAuditCacheRef.current.entries())),
          );
        } catch (err) {
          console.warn(
            "Failed to persist free audit cache to sessionStorage:",
            err,
          );
        }

        console.log(
          `Free audit data cached for ${url} - Score: ${freeAuditData.stats?.score || "N/A"}`,
        );

        // Clean old cache entries (older than 24 hours)
        let cacheChanged = false;
        for (const [key, value] of freeAuditCacheRef.current.entries()) {
          if (Date.now() - value.timestamp > SESSION_CACHE_DURATION.current) {
            freeAuditCacheRef.current.delete(key);
            console.log(`Cleaned expired cache entry for ${key}`);
            cacheChanged = true;
          }
        }

        // Update sessionStorage if cache was cleaned
        if (cacheChanged) {
          try {
            sessionStorage.setItem(
              "freeAuditCache",
              JSON.stringify(Array.from(freeAuditCacheRef.current.entries())),
            );
          } catch (err) {
            console.warn(
              "Failed to update free audit cache in sessionStorage:",
              err,
            );
          }
        }

        return freeAuditData;
      } catch (err) {
        console.error("Error fetching free audit data:", err);
        return null;
      }
    },
    [],
  );

  /**
   * Fetch enhancement audit data
   */
  const fetchEnhancementAudit = useCallback(
    async (
      url?: string,
      forceRefresh = false,
    ): Promise<ComprehensiveEnhancementAudit | null> => {
      try {
        const targetUrl = url || currentBrand?.url;
        if (!targetUrl)
          throw new Error("No URL provided for enhancement audit");
        setEnhancementLoading(true);
        setEnhancementError(null);
        if (!forceRefresh) {
          const cached = enhancementCacheRef.current.get(targetUrl);
          if (
            cached &&
            Date.now() - cached.timestamp < ENHANCEMENT_CACHE_DURATION.current
          ) {
            setEnhancementAudit(cached.audit);
            setEnhancementLoading(false);
            return cached.audit;
          }
        }
        const apiUrl = `${pb.baseURL}/api/v1/seo/enhancement-audit`;
        let pollCount = 0;
        const poll =
          async (): Promise<ComprehensiveEnhancementAudit | null> => {
            const response = await fetch(apiUrl, {
              method: "POST",
              headers: {
                Authorization: pb.authStore.token,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ url: targetUrl }),
            });
            if (response.status === 202) {
              // Pending, poll again after delay
              if (pollCount++ > 30)
                throw new Error(
                  "Audit is taking too long. Please try again later.",
                );
              await new Promise((res) => setTimeout(res, 5000));
              return poll();
            }
            if (!response.ok)
              throw new Error(
                `Enhancement audit failed: ${response.statusText}`,
              );
            const data = await response.json();
            // If the response has a status field and it's pending, poll again
            if (data.status === "pending" || data.status === "processing") {
              if (pollCount++ > 30)
                throw new Error(
                  "Audit is taking too long. Please try again later.",
                );
              await new Promise((res) => setTimeout(res, 5000));
              return poll();
            }
            // Only cache completed audits
            enhancementCacheRef.current.set(targetUrl, {
              audit: data,
              timestamp: Date.now(),
            });
            // Clean old cache entries
            for (const [key, value] of enhancementCacheRef.current.entries()) {
              if (
                Date.now() - value.timestamp >
                ENHANCEMENT_CACHE_DURATION.current
              ) {
                enhancementCacheRef.current.delete(key);
              }
            }
            setEnhancementAudit(data);
            setEnhancementLoading(false);
            return data;
          };
        return await poll();
      } catch (err) {
        console.error("Error fetching enhancement audit:", err);
        setEnhancementError(
          err instanceof Error
            ? err.message
            : "Failed to fetch enhancement audit",
        );
        setEnhancementLoading(false);
        return null;
      }
    },
    [currentBrand, ENHANCEMENT_CACHE_DURATION],
  );

  /**
   * Main effect to fetch or create audit data when the brand changes
   */
  useEffect(() => {
    setAudit(null);
    setLoading(false); // Start with loading false, we'll set it to true in fetchAudit if needed
    setError(null);

    // Also reset enhancement audit data when brand changes
    setEnhancementAudit(null);
    setEnhancementLoading(false);
    setEnhancementError(null);

    if (isBrandLoading) return;
    if (!currentBrand) return;

    // Fetch Free Audit (Overview/Pagespeed)
    setLoading(true);
    fetchFreeAudit(currentBrand.url)
      .then((data) => {
        setAudit(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : "Failed to fetch audit");
        setLoading(false);
      });

    // Fetch Enhancement Audit
    setEnhancementLoading(true);
    fetchEnhancementAudit(currentBrand.url)
      .then((data) => {
        setEnhancementAudit(data);
        setEnhancementLoading(false);
      })
      .catch((err) => {
        setEnhancementError(
          err instanceof Error
            ? err.message
            : "Failed to fetch enhancement audit",
        );
        setEnhancementLoading(false);
      });
  }, [currentBrand, isBrandLoading, fetchFreeAudit, fetchEnhancementAudit]);

  return {
    audit,
    loading,
    error,
    enhancementAudit,
    enhancementLoading,
    enhancementError,
    fetchEnhancementAudit,
  };
}
